// This file is for helpers that relate to any time helpers within the loading module

import * as R from 'ramda';

// Time Helpers
import {
  add,
  differenceInDays,
  differenceInMonths,
  differenceInYears,
  format,
  getMinutes,
  isPast,
  isToday,
  parseISO,
  setMinutes,
} from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import { capitalizeMultiWords } from 'utils/helpers/format';

export const formatTimeToCst = time => utcToZonedTime(time, 'America/Chicago');

export const formatTimeToAmPm = time => format(time, 'h:mm aa');

export const formatTime = (minToAdd, mdUp) => {
  // take current time and add it in minutes
  const time = add(new Date(), { minutes: minToAdd });
  // use CST/CDT time (Chicago is primary city that works in Date FNS)
  const cst = utcToZonedTime(time, 'America/Chicago');
  const min = getMinutes(cst);
  // round times based on the additional minutes added
  const rounded = min >= 30 ? setMinutes(cst, 30) : setMinutes(cst, 0);
  // format based on mobile vs desktop view
  const formatString = mdUp ? 'h:mm' : 'h:mm aa';
  return format(rounded, formatString);
};

export const mapLocationData = arr => {
  const terminals = arr[0].data;
  const waitTimes = arr[1].data;

  // take 2 arrays and map into one
  // set one array to the map object,
  // then the other, comibining objects
  // based on locationId
  const map = new Map();
  terminals.forEach(terminal =>
    map.set(terminal.locationId, {
      name: terminal.name || terminal.nameShort,
      waitTimes: [],
      ...terminal,
    })
  );
  waitTimes.forEach(time => map.set(time.locationId, { ...map.get(time.locationId), ...time }));
  return Array.from(map.values());
};

export const mapStates = arr => {
  const mappedAddresses = R.sortBy(
    R.compose(R.toLower, R.prop('state')),
    arr.map(({ address }) => address)
  );
  const mapVal = ({ state }) => ({
    label: capitalizeMultiWords(state),
    value: capitalizeMultiWords(state),
  });
  const states = R.map(mapVal, R.uniqBy(R.prop('state'), mappedAddresses));
  return [{ label: 'All', value: 'All' }, ...states];
};

// Takes in a military formatted string (14:00) and returns 12h string (2:00 PM)
// convertMilitaryTimeString :: string -> string
export const convertMilitaryTimeString = R.compose(
  // Formats time
  // Add AM if before noon -> 2:00 AM
  // Subtract 12 hours if after noon, add minutes, format to PM -> 2:00 PM
  // Return 12:00 PM if exactly noon -> ->
  R.cond([
    [R.equals(0), R.always('12:00 AM')],
    [time => R.lt(time, 12), time => `${time}:00 AM`],
    [R.equals(12), R.always('12:00 PM')],
    [time => R.gt(time, 12), time => `${time - 12}:00 PM`],
  ]),
  // convert to integer -> 14
  parseInt,
  // Take first value -> '14'
  R.nth(0),
  // Split time to get isolated values -> ['14', '00]
  R.split(':')
  // Example: '14:00'
);

export const formatEndDate = (dateFormat, endDate) =>
  // Will return the empty value to be displayed on the card if the date is the pre-formatted 'No End Date' value
  // Otherwise, will format the display using the endDate and whatever the desired date format string, e.g. M/dd/yy
  endDate === '2999-12-31T23:59:59' ? '--' : format(new Date(endDate), dateFormat);

// would be more DRY and probably slightly faster to have Date.now() as a variable, but that breaks the tests
const daysDiff = d => differenceInDays(Date.now(), new Date(d));
const monthsDiff = d => differenceInMonths(Date.now(), new Date(d));
const yearsDiff = d => differenceInYears(Date.now(), new Date(d));

export const formatLastUsed = R.cond([
  [R.isNil, R.always('--')],
  [R.o(R.gte(R.__, 5), yearsDiff), R.always('>5y')],
  [R.o(R.gte(R.__, 1), yearsDiff), R.compose(R.concat(R.__, 'y'), R.toString, yearsDiff)],
  [R.o(R.gte(R.__, 3), monthsDiff), R.compose(R.concat(R.__, 'm'), R.toString, monthsDiff)],
  [R.T, R.compose(R.concat(R.__, 'd'), R.toString, daysDiff)],
]);

// check to see if date is today or in the past, useful for determining if something is expired
// currently only used in Carrier Auth's CardBody component
export const dateIsTodayOrInPast = R.o(R.either(isPast, isToday), parseISO);
